import { z } from 'zod';

// `a_confirmar` existe em operações históricas importadas. Mantê-lo no schema
// permite exibir o relatório sem presumir a forma de liquidação do contrato.
export const tipoLiquidacaoSchema = z.enum([
  'unica',
  'parcelada',
  'parcela_unica',
  'entrega_produto',
  'a_confirmar',
]);

export const liberacaoSchema = z.object({
  id: z.coerce.number(),
  operacao_id: z.coerce.number(),
  data_prevista: z.string().nullable(),
  data_efetivada: z.string().nullable(),
  valor: z.coerce.number(),
  status: z.enum(['prevista', 'liberada', 'cancelada']),
  referencia_textual: z.string().nullable().optional(),
  previsao_operacional_dias: z.coerce.number().nullable().optional(),
});

export const parcelaSchema = z.object({
  id: z.coerce.number(),
  operacao_id: z.coerce.number(),
  numero: z.coerce.number(),
  data_vencimento: z.string(),
  valor_previsto: z.coerce.number().nullable(),
  percentual_saldo_devedor: z.coerce.number().nullable().optional(),
  valor_pago: z.coerce.number().nullable(),
  data_pagamento: z.string().nullable(),
  status: z.string(),
});

export const depositoArmazemSchema = z.object({
  id: z.coerce.number(),
  operacao_id: z.coerce.number(),
  produto: z.string(),
  quantidade_kg: z.coerce.number().nullable(),
  quantidade_sacas: z.coerce.number().nullable(),
  valor_saca: z.coerce.number().nullable(),
  safra: z.string().nullable(),
  tipo_armazem: z.string().nullable(),
  armazem_nome: z.string().nullable(),
  armazem_endereco: z.string().nullable(),
  armazem_municipio: z.string().nullable(),
  armazem_uf: z.string().nullable(),
  armazem_cep: z.string().nullable(),
  data_deposito: z.string().nullable(),
  fiel_depositario: z.string().nullable(),
  observacoes: z.string().nullable(),
});

export const anexoSchema = z.object({
  id: z.coerce.number(),
  operacao_id: z.coerce.number(),
  categoria: z.string(),
  titulo: z.string(),
  arquivo_path: z.string(),
  mime_type: z.string().nullable(),
  tamanho_bytes: z.coerce.number().nullable(),
  data_documento: z.string().nullable(),
  observacoes: z.string().nullable(),
  created_at: z.string().nullable(),
});

export const seguroSchema = z.object({
  id: z.coerce.number(),
  operacao_id: z.coerce.number(),
  tipo: z.string(),
  produto: z.string().nullable(),
  seguradora: z.string().nullable(),
  seguradora_cnpj: z.string().nullable(),
  apolice: z.string().nullable(),
  susep: z.string().nullable(),
  capital_segurado: z.coerce.number().nullable(),
  premio: z.coerce.number(),
  vigencia_ini: z.string().nullable(),
  vigencia_fim: z.string().nullable(),
  observacoes: z.string().nullable(),
});

export const seguroConformidadeSchema = z.object({
  seguro_id: z.coerce.number(),
  tipo: z.string(),
  produto: z.string().nullable(),
  premio: z.coerce.number(),
  status: z.enum(['ok', 'warn', 'danger']),
  label: z.string(),
  resumo: z.string(),
  pendencias: z.array(z.string()),
  recomendacao: z.string(),
});

export const garantiaSchema = z.object({
  id: z.coerce.number(),
  operacao_id: z.coerce.number(),
  tipo: z.string(),
  produto: z.string().nullable(),
  quantidade_kg: z.coerce.number().nullable(),
  quantidade_sacas: z.coerce.number().nullable(),
  valor_saca: z.coerce.number().nullable(),
  area_plantada_ha: z.coerce.number().nullable(),
  grau_hipoteca: z.coerce.number().nullable(),
  valor_estimado: z.coerce.number().nullable(),
  imovel_id: z.coerce.number().nullable(),
  imovel_matricula: z.string().nullable(),
  imovel_denominacao: z.string().nullable(),
  valor_avaliacao: z.coerce.number().nullable(),
  descricao: z.string().nullable(),
  // Penhor de animais
  quantidade_cabecas: z.coerce.number().nullable().optional(),
  valor_unitario: z.coerce.number().nullable().optional(),
  categoria_animal: z.string().nullable().optional(),
  raca_animal: z.string().nullable().optional(),
  idade_min_meses: z.coerce.number().nullable().optional(),
  idade_max_meses: z.coerce.number().nullable().optional(),
  marca_descricao: z.string().nullable().optional(),
});

export const orcamentoItemSchema = z.object({
  id: z.coerce.number(),
  operacao_id: z.coerce.number(),
  grupo: z.string().nullable(),
  discriminacao: z.string(),
  unidade: z.string().nullable(),
  quantidade: z.coerce.number().nullable(),
  valor_unitario: z.coerce.number().nullable(),
  valor_total: z.coerce.number().nullable(),
  recursos_proprios: z.coerce.number().nullable(),
  valor_financiado: z.coerce.number().nullable(),
  epoca: z.string().nullable(),
  ordem: z.coerce.number().nullable(),
});

export const participanteSchema = z.object({
  id: z.coerce.number(),
  operacao_id: z.coerce.number(),
  cliente_id: z.coerce.number().nullable(),
  papel: z.string(),
  nome: z.string().nullable(),
  cpf: z.string().nullable(),
  observacoes: z.string().nullable(),
  cliente_nome_link: z.string().nullable().optional(),
});

export const operacaoSchema = z.object({
  id: z.coerce.number(),
  cliente_id: z.coerce.number(),
  imovel_id: z.coerce.number().nullable(),
  numero_cedula: z.string(),
  tipo_instrumento: z.string(),
  modalidade: z.string(),
  instituicao_id: z.coerce.number().nullable(),
  instituicao_nome: z.string().nullable(),
  // Rota de impressao (_print/relatorio-operacao) — vindos do join no detalhe.
  cliente_nome: z.string().nullable().optional(),
  cliente_cpf: z.string().nullable().optional(),
  instituicao_cor: z.string().nullable(),
  programa_linha_id: z.coerce.number().nullable(),
  programa_nome: z.string().nullable(),
  programa_pipe: z.string().nullable(),
  agencia_nome: z.string().nullable(),
  ref_bacen: z.string().nullable(),
  cultura: z.string().nullable(),
  periodo_agricola_ini: z.string().nullable(),
  periodo_agricola_fim: z.string().nullable(),
  area_ha: z.coerce.number().nullable(),
  valor_total: z.coerce.number(),
  data_contratacao: z.string().nullable(),
  data_vencimento: z.string(),
  taxa_juros_aa: z.coerce.number().nullable(),
  taxa_juros_aa_sem_bonus: z.coerce.number().nullable().optional(),
  recursos_proprios: z.coerce.number().nullable().optional(),
  recurso: z.string().nullable().optional(),
  setor: z.string().nullable().optional(),
  subfinalidade: z.string().nullable().optional(),
  tipo_liquidacao: tipoLiquidacaoSchema,
  num_parcelas: z.coerce.number(),
  carencia_meses: z.coerce.number().nullable(),
  adesao_proagro: z.boolean(),
  tarifa_estudo: z.coerce.number().nullable(),
  status: z.string(),
  observacoes: z.string().nullable(),
  imovel_denominacao: z.string().nullable(),
  imovel_matricula_ref: z.string().nullable(),
  // campos extras na listagem
  parcelas_pendentes: z.coerce.number().optional(),
  valor_em_aberto: z.coerce.number().nullable().optional(),
  saldo_bancario_vincendo: z.coerce.number().nullable().optional(),
  saldo_bancario_referencia: z.string().nullable().optional(),
  premio_seguros_total: z.coerce.number().nullable().optional(),
  proxima_parcela_data: z.string().nullable().optional(),
  proxima_parcela_valor: z.coerce.number().nullable().optional(),
  // campos extras no detalhe
  liberacoes: z.array(liberacaoSchema).optional(),
  parcelas: z.array(parcelaSchema).optional(),
  garantias: z.array(garantiaSchema).optional(),
  seguros: z.array(seguroSchema).optional(),
  conformidade_seguros: z.array(seguroConformidadeSchema).optional(),
  depositos: z.array(depositoArmazemSchema).optional(),
  anexos: z.array(anexoSchema).optional(),
  participantes: z.array(participanteSchema).optional(),
  orcamento_itens: z.array(orcamentoItemSchema).optional(),
});

export const operacoesListSchema = z.array(operacaoSchema);

export type Operacao = z.infer<typeof operacaoSchema>;
export type Liberacao = z.infer<typeof liberacaoSchema>;
export type Parcela = z.infer<typeof parcelaSchema>;
export type Garantia = z.infer<typeof garantiaSchema>;
export type Seguro = z.infer<typeof seguroSchema>;
export type SeguroConformidade = z.infer<typeof seguroConformidadeSchema>;
export type DepositoArmazem = z.infer<typeof depositoArmazemSchema>;
export type Anexo = z.infer<typeof anexoSchema>;
export type Participante = z.infer<typeof participanteSchema>;
export type OrcamentoItem = z.infer<typeof orcamentoItemSchema>;
